import {
  FormattedPrice,
  PriceOfferType,
  PriceParts,
} from '../services/Appointment/model';

// Implementation of rounding numbers taken from Edit 3 solution at
// https://stackoverflow.com/a/15762794
const roundValue = (value: number, nbrOfDecimals: number): number => {
  const factor = 10 ** nbrOfDecimals;
  const n = parseFloat((value * factor).toFixed(11));
  const result = Math.round(n) / factor;
  return +result.toFixed(nbrOfDecimals);
};

const DECIMAL_RULES = {
  WHEN_NO_DECIMAL: {
    SHOW_NONE: undefined,
    SHOW_DASH: '-',
    SHOW_TWO: '00',
  },
  WHEN_ONE_DECIMAL: {
    SHOW_ONE: (value: string) => value,
    SHOW_TWO: (value: string) => value.padEnd(2, '0'),
  },
};

const getDecimalValue = (
  decimalConfig: CurrencyDecimals,
  decimalValue: string,
): string | undefined => {
  const {
    displayRuleWhenNoDecimalInPrice,
    displayRuleWhenOneDecimalInPrice,
    isZeroDecimalCurrency,
  } = decimalConfig;

  if (isZeroDecimalCurrency) {
    return undefined;
  }

  const decimalValueAsNumber = Number(decimalValue);
  if (decimalValueAsNumber === 0) {
    return DECIMAL_RULES.WHEN_NO_DECIMAL[
      displayRuleWhenNoDecimalInPrice || 'SHOW_TWO'
    ];
  }

  const nbrOfDecimalPlaces = decimalValue.length;
  if (nbrOfDecimalPlaces === 1) {
    const ruleFunc =
      DECIMAL_RULES.WHEN_ONE_DECIMAL[
        displayRuleWhenOneDecimalInPrice || 'SHOW_TWO'
      ];
    return ruleFunc(decimalValue);
  }

  return decimalValue;
};

const getIntegerValueWithSeparator = (
  value: string,
  thousandsSeparator: string,
) => {
  const groupsOfThousands = [];
  const nbrOfDigits = value.length;

  for (let i = nbrOfDigits - 1; i >= 0; i -= 3) {
    const startIndexOfGroup = Math.max(0, i - 2);
    const endIndexOfGroup = i + 1;
    const group = value.slice(startIndexOfGroup, endIndexOfGroup);
    groupsOfThousands.unshift(group);
  }

  return groupsOfThousands.join(thousandsSeparator);
};

const adjustPriceOnWrongDecimalCount = (
  price: number,
  decimalConfig?: CurrencyDecimals,
) => {
  const { isZeroDecimalCurrency } = decimalConfig || {};
  const maxDecimalPlaces = isZeroDecimalCurrency ? 0 : 2;
  const [, decimalPart] = price.toString().split('.');
  if (!decimalPart || decimalPart.length <= maxDecimalPlaces) {
    return price;
  }
  return roundValue(price, maxDecimalPlaces);
};

const getPriceParts = (price: number, isNewCurrency = false): PriceParts => {
  const {
    currency,
    decimals = {},
    separators,
  } = isNewCurrency
    ? CSS_CONFIG.PRICES?.currencyTransition?.newCurrencyFormat || {}
    : CSS_CONFIG.PRICES?.format || {};

  const priceWithCorrectDecimals = adjustPriceOnWrongDecimalCount(
    price,
    decimals,
  );
  const [integerPart, decimalPart] = priceWithCorrectDecimals
    .toString()
    .split('.');

  const integerValue = separators?.thousandsPart
    ? getIntegerValueWithSeparator(integerPart, separators.thousandsPart)
    : integerPart;

  const decimalValue = getDecimalValue(decimals, decimalPart || '0');

  const currencyParts = currency
    ? {
        currencyLabel:
          currency.hideOnDecimalValue && decimalValue ? '' : currency.sign,
        currencyPosition: currency.position,
        currencySpacing: currency.spacing,
        currencyVerticalAlignment: currency.verticalAlignment,
      }
    : {};

  return {
    ...currencyParts,
    decimalSign: separators?.decimalPart || '.',
    decimalValue,
    decimalVerticalAlignment: decimals?.verticalAlignment || 'super',
    integerValue,
  };
};

type Operator = 'DIV' | 'MUL';

const convertCurrency = (
  value: number,
  rate: number,
  operator: Operator,
  decimalPlaces: number,
): number => {
  const result = operator === 'DIV' ? value / rate : value * rate;
  return roundValue(result, decimalPlaces);
};

const getConversionSpecifics = (
  currencyCode: string,
  currencyTransition: CurrencyTransition,
) => {
  const { newCurrency } = currencyTransition;
  const isPriceCurrencyNewCurrency = currencyCode === newCurrency.currencyCode;
  const operator: Operator = isPriceCurrencyNewCurrency ? 'MUL' : 'DIV';

  return {
    isPriceCurrencyNewCurrency,
    operator,
  };
};

const getDualFormattedPrice = (
  price: number,
  currencyCode: string,
  currencyTransition: CurrencyTransition,
): FormattedPrice => {
  const {
    exchangeRateNewToOld,
    oldCurrency,
    newCurrency,
    currencyDisplayOrder,
  } = currencyTransition;
  const { operator, isPriceCurrencyNewCurrency } = getConversionSpecifics(
    currencyCode,
    currencyTransition,
  );
  const convertedPrice = convertCurrency(
    price,
    exchangeRateNewToOld,
    operator,
    isPriceCurrencyNewCurrency
      ? oldCurrency.decimalPlaces
      : newCurrency.decimalPlaces,
  );
  const newPrice = isPriceCurrencyNewCurrency ? price : convertedPrice;
  const oldPrice = isPriceCurrencyNewCurrency ? convertedPrice : price;

  const mainPrice =
    currencyDisplayOrder === 'NEW_CURRENCY_AS_MAIN_PRICE'
      ? getPriceParts(newPrice, true)
      : getPriceParts(oldPrice);
  const secondaryPrice =
    currencyDisplayOrder === 'NEW_CURRENCY_AS_MAIN_PRICE'
      ? getPriceParts(oldPrice)
      : getPriceParts(newPrice, true);
  return {
    mainPrice,
    secondaryPrice,
  };
};

export const getFormattedPrice = (
  price: number,
  currencyCode?: string,
): FormattedPrice => {
  const { currencyTransition } = CSS_CONFIG.PRICES || {};
  if (currencyTransition && currencyTransition.enabled && currencyCode) {
    return getDualFormattedPrice(price, currencyCode, currencyTransition);
  }

  return {
    mainPrice: getPriceParts(price),
  };
};

export const isIkeaMembershipSavings = (
  priceOfferType?: PriceOfferType,
): boolean => priceOfferType === 'family' || priceOfferType === 'ibn';
